import { useEffect, useState } from 'react'
import { todayISO } from '../data/workdate.js'
import PhotoStrip from '../components/PhotoStrip.jsx'
import './home.css'

import menuIcon from '../assets/figma/home/menu.svg'
import bellIcon from '../assets/figma/home/bell-dot.svg'
import dotNotActive from '../assets/figma/home/status-dot-notactive.svg'
import dotActive from '../assets/figma/home/status-dot-active.svg'
import pinIcon from '../assets/figma/home/location-pin.svg'
import chevron from '../assets/figma/home/chevron-down.svg'
import icWorkday from '../assets/figma/home/row-workday.svg'
import icAssignments from '../assets/figma/home/row-assignments.svg'
import icDailyReport from '../assets/figma/home/row-dailyreport.svg'
import icMaterials from '../assets/figma/home/row-materials.svg'
import icTakePhoto from '../assets/figma/home/row-takephoto.svg'
import icAddReceipt from '../assets/figma/home/row-addreceipt.svg'
import icMore from '../assets/figma/home/row-more.svg'
import icExpand from '../assets/figma/home/expand.svg'
import icMessage from '../assets/figma/home/comm-message.svg'
import icMic from '../assets/figma/home/comm-mic.svg'
import icPhone from '../assets/figma/home/comm-phone.svg'

/*
 * Employee Home — implemented from the approved Figma prototype, frames
 * 3:18 (Not Active) and 2:2 (Active). Structure, spacing, type and colour are
 * the Figma values (see home.css). Icons are the exported Figma assets,
 * committed to src/assets/figma/home — never hand-drawn.
 *
 * Employee Home Experience v1.0, A1: Home is a launcher, status surface and
 * quick-preview surface. Tapping a capability opens its full-screen
 * experience; the chevron expands a preview in place. Build 001 wires
 * WORKDAY. The other rows render as designed and are inert for now — each is
 * an Upgrade Register item, not a redesign.
 *
 * TODAY'S ASSIGNMENT is NEW / NOT YET FIGMA-APPROVED — see home.css.
 */
export default function Home({ adapter, session, day, onWorkday, onDailyReport, onTakePhoto }) {
  const [assignment, setAssignment] = useState(undefined) // undefined = loading, null = none
  // BUILD 003 — Employee Home Experience v1.0 A3: the expanded state carries
  // the description and attachments. Collapsed by default so Home stays a
  // launcher and the approved layout is unchanged until the worker asks.
  const [detailOpen, setDetailOpen] = useState(false)

  const employeeId = session?.employee?.id ?? null
  useEffect(() => {
    let alive = true
    if (!employeeId) { setAssignment(null); return }
    adapter.getAssignmentForDate(employeeId, todayISO())
      .then((a) => { if (alive) setAssignment(a) })
      .catch(() => { if (alive) setAssignment(null) })
    return () => { alive = false }
  }, [adapter, employeeId])

  const working = day?.status === 'working'
  const firstName = firstNameOf(session?.employee?.name)
  const activeProject = day?.session?.project ?? null
  const activeLine = activeProject
    ? [activeProject.name, activeProject.location].filter(Boolean).join(', ')
    : 'Project not yet matched'
  const since = day?.checkedInAt ? timeLabel(day.checkedInAt) : null

  return (
    <div className="eh" data-figma="3:18 / 2:2">
      <div className="eh-nav">
        <button type="button" aria-label="Menu"><img className="eh-nav__menu" src={menuIcon} alt="" /></button>
        <button type="button" aria-label="Notifications"><img className="eh-nav__bell" src={bellIcon} alt="" /></button>
      </div>

      <div className="eh-greeting">
        <p>{greetingFor(new Date())},</p>
        <p>{firstName ? `${firstName}.` : ''}</p>
      </div>

      {/*
        NEW / NOT YET FIGMA-APPROVED — today's expected Project, per Build 001.

        BUILD 003: this no longer hides once the worker is checked in. The
        Assignment now carries the instructions and photos for the job, and a
        crew needs those *while* they are working, not only before they start.
        It sits above the ACTIVE card rather than replacing it, because the two
        say different things — what was expected, and what is happening — and
        the Independent Facts Principle is that neither stands in for the other.
      */}
      {assignment !== undefined && (
        <div className="eh-status-wrap" style={{ paddingBottom: 12 }}>
          <div className="eh-card eh-card--assignment" data-figma="none — Build 001 extension">
            <div className="eh-card__text">
              <div className="eh-card__label">
                <p className="eh-card__title">TODAY&rsquo;S ASSIGNMENT</p>
              </div>
              {assignment ? (
                <>
                  <p className="eh-card__line">{assignment.project.name}</p>
                  {assignment.project.location && (
                    <p className="eh-card__line eh-card__muted">{assignment.project.location}</p>
                  )}
                  {/* BUILD 003 — the work itself, not just where to be. */}
                  {assignment.title && (
                    <p className="eh-assign__title">{assignment.title}</p>
                  )}
                  {(assignment.description || assignment.photoIds?.length > 0) && (
                    <>
                      <button type="button" className="eh-assign__toggle"
                              aria-expanded={detailOpen}
                              onClick={() => setDetailOpen((v) => !v)}>
                        {detailOpen ? 'Hide details' : 'View details'}
                        {assignment.photoIds?.length > 0 && !detailOpen
                          ? ` · ${assignment.photoIds.length} photo${assignment.photoIds.length === 1 ? '' : 's'}`
                          : ''}
                      </button>
                      {detailOpen && (
                        <div className="eh-assign__detail">
                          {assignment.description && (
                            <p className="eh-assign__desc">{assignment.description}</p>
                          )}
                          <PhotoStrip photoIds={assignment.photoIds ?? []} />
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <p className="eh-card__line eh-card__muted">No assignment for today.</p>
              )}
            </div>
            {assignment && <img className="eh-card__pin" src={pinIcon} alt="" />}
          </div>
        </div>
      )}

      <div className="eh-status-wrap">
        {working ? (
          <div className="eh-card eh-card--active" data-figma="2:23">
            <div className="eh-card__text">
              <div className="eh-card__label">
                <img className="eh-card__dot" src={dotActive} alt="" />
                <p className="eh-card__title">ACTIVE</p>
              </div>
              <p className="eh-card__line">{activeLine}</p>
              {since && <p className="eh-card__line">Since {since}</p>}
            </div>
            <img className="eh-card__pin" src={pinIcon} alt="" />
          </div>
        ) : (
          <div className="eh-card eh-card--notactive" data-figma="3:41">
            <div className="eh-card__text">
              <div className="eh-card__label">
                <img className="eh-card__dot" src={dotNotActive} alt="" />
                <p className="eh-card__title">NOT ACTIVE</p>
              </div>
              <p className="eh-card__line">You are not checked in.</p>
              <p className="eh-card__line">Tap Workday to get started.</p>
            </div>
          </div>
        )}
      </div>

      <div className="eh-rows-wrap">
        <div className="eh-rows" data-figma="3:52">
          <Row icon={icWorkday}     label="WORKDAY"      onClick={onWorkday} />
          <Row icon={icAssignments} label="ASSIGNMENTS" />
          <Row icon={icDailyReport} label="DAILY REPORT" onClick={onDailyReport} />
          <Row icon={icMaterials}   label="MATERIALS" />
          {/* TAKE PHOTO opens Daily Report with the camera already up. Frame
              7:2's custom viewfinder is deferred whole rather than
              reinterpreted, and a photo belongs to the day's report anyway —
              which is where it becomes visible, and where an acknowledgement
              has somewhere to live. Home (3:18) gains no new element. */}
          <Row icon={icTakePhoto}   label="TAKE PHOTO"   onClick={onTakePhoto} />
          <Row icon={icAddReceipt}  label="ADD RECEIPT" />
          <Row icon={icMore}        label="MORE" />
        </div>
      </div>

      <div className="eh-expand-wrap">
        <button type="button" className="eh-expand" aria-label="Open messages"><img src={icExpand} alt="" /></button>
      </div>

      <div className="eh-comm-wrap">
        <div className="eh-comm" data-figma="3:114">
          <img className="eh-comm__msg" src={icMessage} alt="" />
          <p className="eh-comm__placeholder">Message...</p>
          <img className="eh-comm__icon" src={icMic} alt="" />
          <img className="eh-comm__icon" src={icPhone} alt="" />
        </div>
      </div>
    </div>
  )
}

function Row({ icon, label, onClick }) {
  return (
    <button type="button" className="eh-row" onClick={onClick} disabled={!onClick} style={!onClick ? { cursor: 'default' } : undefined}>
      <img className="eh-row__icon" src={icon} alt="" />
      <p className="eh-row__label">{label}</p>
      <img className="eh-row__chevron" src={chevron} alt="" />
    </button>
  )
}

/*
 * Time-of-day greeting. The Figma frame shows "Good morning," on a screen
 * whose status bar reads 9:41 — read here as a sample value, like "Caio" and
 * "300 Main Street", not as the literal string at 4 PM. Commit 48936aa earlier
 * removed time-of-day copy after field testing; that conflict is recorded in
 * the Build 001 Upgrade Register for Founder decision. Same typography and
 * layout either way.
 */
function greetingFor(d) {
  const h = d.getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

function firstNameOf(name) {
  return String(name || '').trim().split(/\s+/)[0] || ''
}

function timeLabel(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) } catch { return '' }
}
